import type { ObjecttypeId, ObjecttypeIdString } from 'cadenza/repository/model/objecttype-id.gen';
import type { RootStateOr } from 'cadenza/store/cadenza-store';
import type { ReportId } from 'cadenza/workbook-report/dto/report-id';
import type { WorkbookViewSettings } from 'cadenza/workbook-report/model/report-settings';
import type { ActiveAttributeId } from 'cadenza/workbook/api/active-attribute-id';
import type { BasicObjecttypeReachabilityDto } from 'cadenza/workbook/api/basic-objecttype-reachability-dto';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import type { WorksheetId } from 'cadenza/workbook/api/worksheet-id';
import type { AttributeId } from 'cadenza/workbook/model/attribute-id.gen';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import { getAttributes, getObjecttypeById } from 'cadenza/workbook/workbook-core-selectors.state';
import { slice } from 'cadenza/workbook/workbook-core-slice.state';
import type { WorkbookState } from 'cadenza/workbook/workbook.state';

abstract class ReachableSetsCache {
  #state: RootStateOr<WorkbookState>;
  #reachablesRetriever: (ot: BasicObjecttypeReachabilityDto) => ObjecttypeIdString[];
  #objecttypeReachability: Map<ObjecttypeIdString, Set<ObjecttypeIdString>>;

  constructor(
    state: RootStateOr<WorkbookState>,
    reachablesRetriever: (ot: BasicObjecttypeReachabilityDto) => ObjecttypeIdString[],
  ) {
    this.#state = state;
    this.#reachablesRetriever = reachablesRetriever;
    this.#objecttypeReachability = new Map();
  }

  getReachablesSet(sourceBotId: ObjecttypeIdString): Set<ObjecttypeIdString> {
    const cachedReachables = this.#objecttypeReachability.get(sourceBotId);
    if (cachedReachables) {
      return cachedReachables;
    }
    const reachables = getBasicReachables(this.#state, sourceBotId, this.#reachablesRetriever);
    this.#objecttypeReachability.set(sourceBotId, reachables);
    return reachables;
  }
}

export class ToAnyReachableSetsCache extends ReachableSetsCache {
  constructor(state: RootStateOr<WorkbookState>) {
    super(state, (ot: BasicObjecttypeReachabilityDto) => ot.reachableByToAny);
  }
}

class ToOneReachableSetsCache extends ReachableSetsCache {
  constructor(state: RootStateOr<WorkbookState>) {
    super(state, (ot: BasicObjecttypeReachabilityDto) => ot.reachableByToOne);
  }
}

/**
 * This selector checks if an attribute is "reachable" by non-multiplying associations
 * from a given attribute.
 * A target attribute is considered reachable by non-multiplying associations from an origin
 * attribute if the origin's OT and the target's OT are connected via one-to-one or many-to-one
 * associations as seen from the origin's OT.
 * If one of the OTs is a ZOT, then the underlying bOTs of the attribute are considered instead,
 * as "reachability" cannot be accurately computed for zOT.
 * All the reachability is computed on the backend and included in the workbook state.
 * See <code>net.disy.cadenza.workbook.model.impl.CachingReachableObjecttypeCalculator</code>
 */
export function isAttributeToOneReachableFrom(
  state: RootStateOr<WorkbookState>,
  originAttribute: WorkbookAttributeDto,
  targetAttribute: WorkbookAttributeDto,
) {
  return predicateAttributeToOneReachableFrom(state, originAttribute)(targetAttribute);
}

export function predicateAttributeToOneReachableFrom(
  state: RootStateOr<WorkbookState>,
  originAttribute: WorkbookAttributeDto,
) {
  const availableBasicObjecttypes = new Set(generateReachableBasicObjecttypeIds(state, originAttribute));
  return (targetAttribute: WorkbookAttributeDto) =>
    isAttributeReachableByReachabilitySet(targetAttribute, availableBasicObjecttypes);
}

function* generateReachableBasicObjecttypeIds(
  state: RootStateOr<WorkbookState>,
  originAttribute: WorkbookAttributeDto,
) {
  const reachableSetsCache = new ToOneReachableSetsCache(state);
  for (const originOtId of getUnderlyingBasicObjecttypeIds(originAttribute)) {
    yield* reachableSetsCache.getReachablesSet(originOtId);
  }
}

export const filterAttributesToOneReachableFrom = (
  state: RootStateOr<WorkbookState>,
  originBasicObjecttypeId: ObjecttypeIdString,
  targetAttributes: WorkbookAttributeDto[],
) => {
  const reachableSetsCache = new ToOneReachableSetsCache(state);
  const availableBasicObjecttypes = reachableSetsCache.getReachablesSet(originBasicObjecttypeId);
  return targetAttributes.filter((targetAttribute) =>
    isAttributeReachableByReachabilitySet(targetAttribute, availableBasicObjecttypes),
  );
};

const isAttributeAcceptedFrom = (
  originAttribute: WorkbookAttributeDto,
  targetAttribute: WorkbookAttributeDto,
  reachableSetsCache: ReachableSetsCache,
) => {
  if (originAttribute.objecttypeId === targetAttribute.objecttypeId) {
    return true;
  }

  // O(|ot|) where |ot| is the number of reachable Objecttypes
  const originOtIds = getUnderlyingBasicObjecttypeIds(originAttribute);

  const availableBasicObjecttypes: Set<ObjecttypeIdString> = new Set();
  for (let originOtId of originOtIds) {
    reachableSetsCache.getReachablesSet(originOtId).forEach((ot) => availableBasicObjecttypes.add(ot));
  }
  return isAttributeAcceptedByReachabilitySet(targetAttribute, availableBasicObjecttypes);
};

const getBasicReachables = (
  state: RootStateOr<WorkbookState>,
  sourceBasicObjecttypeId: ObjecttypeIdString,
  reachables: (item: BasicObjecttypeReachabilityDto) => ObjecttypeIdString[],
) => {
  const reachableObjecttypesById = slice(state).reachableObjecttypesByBasicObjecttypeId;
  const reachableObjecttypes = reachableObjecttypesById[sourceBasicObjecttypeId];
  return reachableObjecttypes ? new Set(reachables(reachableObjecttypes)) : new Set<ObjecttypeIdString>();
};

const isAttributeReachableByReachabilitySet = (
  targetAttribute: WorkbookAttributeDto,
  reachableObjecttypeIds: Set<ObjecttypeIdString>,
) => {
  // O(|ot|) where |ot| is the number of reachable Objecttypes
  const targetOtIds = getUnderlyingBasicObjecttypeIds(targetAttribute);
  return targetOtIds.every((otId) => reachableObjecttypeIds.has(otId));
};

const getUnderlyingBasicObjecttypeIds = (member: WorkbookAttributeDto) => {
  // For hierarchies (which have levels), check only the first level for now, as the
  // method is used for maps where only one level is relevant. The highest level can
  // always become selected later and should have the coarsest granularity, so allowing
  // another attribute as "to-one-reachable" by a lower level might lead to errors when
  // the hierarchy is switched to a coarser level.
  return member.levels ? member.levels[0].underlyingObjecttypeIds : member.underlyingObjecttypeIds;
};

/**
 * Return all attributes which are reachable by the main OT of the specified workbook's views.
 */
export const getReachableAttributesByWorksheetId = (
  state: RootStateOr<WorkbookState>,
  worksheetId: WorksheetId,
): WorkbookAttributeDto[] => {
  return [
    ...new Set(
      // Since every worksheet could list the same attributes (e.g. when copy) we deduplicate
      slice(state).worksheetById[worksheetId].views.flatMap((viewId) => getReachableAttributesByViewId(state, viewId)),
    ),
  ];
};

/**
 * Return all attributes which are reachable by the main OT of the specified view.
 */
export const getReachableAttributesByViewId = (
  state: RootStateOr<WorkbookState>,
  viewId: ViewId,
): WorkbookAttributeDto[] => {
  return [
    ...new Set(
      // Since every dataView could list the same attributes (e.g. when copy) we deduplicate
      slice(state).viewById[viewId].dataViews.flatMap((dataViewId) =>
        getReachableAttributesByDataViewId(state, dataViewId),
      ),
    ),
  ];
};

/**
 * Return all attributes which are reachable by the main OT of the specified workbook's views.
 */
export const getReachableAttributesByReportId = (
  state: RootStateOr<WorkbookState>,
  reportId: ReportId,
): WorkbookAttributeDto[] => {
  return [
    ...new Set(
      // Since every worksheet could list the same attributes (e.g. when copy) we deduplicate
      slice(state)
        .reportById[reportId].contents.filter((reportItem) => reportItem.type === 'WorkbookView')
        .flatMap((reportItem) =>
          getReachableAttributesByViewId(state, (reportItem.settings as WorkbookViewSettings)['WorkbookView.viewId']),
        ),
    ),
  ];
};

/**
 * Return ids of all attributes which are reachable by the main OT of the specified data view.
 */
export const getAcceptedAttributeIdsByDataViewId = (
  state: RootStateOr<WorkbookState>,
  dataViewId: DataViewId,
): (AttributeId | ActiveAttributeId)[] => {
  const dataView = slice(state).dataViewById[dataViewId];
  const dataViewMainObjecttypeId = dataView.mainObjecttype.objecttypeId;
  return getAcceptedAttributeIdsByObjectTypeId(state, dataViewMainObjecttypeId);
};

export const getAcceptedAttributeIdsByObjectTypeId = (
  state: RootStateOr<WorkbookState>,
  objecttypeId: ObjecttypeId,
) => {
  if (!objecttypeId) {
    return getAttributes(state).map((attribute) => attribute.id);
  }
  const objecttype = getObjecttypeById(state, objecttypeId);
  if (!objecttype) {
    return getAttributes(state).map((attribute) => attribute.id);
  }

  const toAnyReachableCache = new ToAnyReachableSetsCache(state);
  const basicMainObjecttypeId = objecttype.underlyingBasicMainObjecttypeId;
  return getAttributes(state)
    .filter((candidate) => isAttributeAcceptedForDataView(candidate, basicMainObjecttypeId, toAnyReachableCache))
    .map((attribute) => attribute.id);
};

export const isAttributeAcceptedForDataView = (
  candidate: WorkbookAttributeDto,
  basicMainObjecttypeId: ObjecttypeIdString,
  reachableSetsCache: ReachableSetsCache,
): boolean => {
  return (
    isAttributeAcceptedByReachabilitySet(candidate, reachableSetsCache.getReachablesSet(basicMainObjecttypeId)) ||
    isObjecttypeReachableByReachabilitySet(candidate, basicMainObjecttypeId, reachableSetsCache)
  );
};

const isAttributeAcceptedByReachabilitySet = (
  targetAttribute: WorkbookAttributeDto,
  reachabilitySet: Set<ObjecttypeIdString>,
) => {
  // O(|ot|) where |ot| is the number of reachable Objecttypes
  const relevantBotIds = getRelevantBotIdsToCheck(targetAttribute);
  return relevantBotIds.every((botId) => reachabilitySet.has(botId));
};

const isObjecttypeReachableByReachabilitySet = (
  sourceAttribute: WorkbookAttributeDto,
  targetObjecttypeId: ObjecttypeIdString,
  reachableSetsCache: ReachableSetsCache,
): boolean => {
  const relevantBotIds = getRelevantBotIdsToCheck(sourceAttribute);
  return relevantBotIds.every((botId) => reachableSetsCache.getReachablesSet(botId).has(targetObjecttypeId));
};

const getRelevantBotIdsToCheck = (attribute: WorkbookAttributeDto): ObjecttypeIdString[] => {
  return attribute.levels ? attribute.levels[0].allBotIdsFromPaths : attribute.allBotIdsFromPaths;
};

export const isAttributeAcceptedForQlCondition = (
  currentAttributes: WorkbookAttributeDto[],
  candidate: WorkbookAttributeDto,
  reachableSetsCache: ReachableSetsCache,
): boolean => {
  return (
    isCandidateReachableFromAnyCurrentAttribute(currentAttributes, candidate, reachableSetsCache) ||
    isEveryCurrentAttributeReachableFromCandidate(currentAttributes, candidate, reachableSetsCache)
  );
};

const isCandidateReachableFromAnyCurrentAttribute = (
  currentAttributes: WorkbookAttributeDto[],
  candidate: WorkbookAttributeDto,
  reachableSetsCache: ReachableSetsCache,
): boolean => {
  return currentAttributes.some((currentAttr) => isAttributeAcceptedFrom(currentAttr, candidate, reachableSetsCache));
};

const isEveryCurrentAttributeReachableFromCandidate = (
  currentAttributes: WorkbookAttributeDto[],
  candidate: WorkbookAttributeDto,
  reachableSetsCache: ReachableSetsCache,
): boolean => {
  return currentAttributes.every((currentAttr) => isAttributeAcceptedFrom(candidate, currentAttr, reachableSetsCache));
};

/**
 * Return all attributes which are reachable by the main OT of the specified data view.
 */
export const getReachableAttributesByDataViewId = (
  state: RootStateOr<WorkbookState>,
  dataViewId: DataViewId,
): WorkbookAttributeDto[] => {
  const dataView = slice(state).dataViewById[dataViewId];
  const objecttypeId = dataView.mainObjecttype.objecttypeId;
  return getReachableAttributesByObjecttypeId(state, objecttypeId);
};

const getReachableAttributesByObjecttypeId = (
  state: RootStateOr<WorkbookState>,
  objecttypeId: ObjecttypeId,
): WorkbookAttributeDto[] => {
  const objecttype = getObjecttypeById(state, objecttypeId);
  if (!objecttype) {
    return getAttributes(state);
  }
  const toAnyReachableCache = new ToAnyReachableSetsCache(state);
  const basicMainObjecttypeId = objecttype.underlyingBasicMainObjecttypeId;
  const reachableByToAnyObjecttypeIds = toAnyReachableCache.getReachablesSet(basicMainObjecttypeId);
  return getAttributes(state).filter((attribute) =>
    isAttributeReachableByReachabilitySet(attribute, reachableByToAnyObjecttypeIds),
  );
};
